/**
 * Contract columns -> AG Grid column definitions.
 *
 * The contract's `ScreenerConfig.columns` is a *view*, never a whitelist: screen
 * result schemas are dynamic, the server always sends every column, and the user
 * can show or hide any of them. So the column set is the union of
 *
 *   1. configured entries, in their configured order, and
 *   2. every remaining field discovered in the Arrow schema, hidden.
 *
 * That union is what makes show/hide a pure client-side toggle — a hidden column
 * is already present as a `ColDef` with `hide: true`, so revealing it never needs
 * a server round-trip. It also means a configured column whose field the upstream
 * engine dropped simply disappears instead of erroring.
 *
 * Everything here is AG Grid **Community**. No Set Filter, no row grouping, no
 * `aggFunc` — rollups arrive as their own frames from the server.
 */

import type { ColDef } from "ag-grid-community";
import type {
  ScreenerCellStyle,
  ScreenerColumn,
  ScreenerFilterNode,
  ScreenerFormatRule,
} from "@qufolio/model";
import {
  formatTimeframe,
  type ScreenerColumnMeta,
  type ScreenerField,
  type ScreenerFieldType,
  type ScreenerRow,
} from "@qufolio/transport";

import { collectFields, evaluate, needsHistory } from "./expr.js";
import { columnBand, freshnessOf, markClass, type CategoryOf } from "./bands.js";
import { DEFAULT_PRECISION, effectiveNotation, formatNotation } from "./notation.js";
import { BooleanCell } from "../components/BooleanCell.js";
import { NewsCell } from "../components/NewsCell.js";
import { isNewsField } from "./news-cell.js";
import { ImageCell, imageFields } from "../components/ImageCell.js";
import { evaluateFilterNode, testPredicate } from "./filter-eval.js";

/** Resolves a theme token (e.g. `chart.up`) to a CSS color. */
export type TokenResolver = (token: string) => string;

/** Colors are semantic tokens resolved at render time (D13), never baked in. */
const passthroughResolver: TokenResolver = (token) => token;

/**
 * How a column renders. Extends the Arrow-inferred types with `image`, which has
 * no Arrow counterpart — it is always an explicit contract choice.
 */
export type RenderType = ScreenerFieldType | "image";

/** Community filter for each render type. */
function filterFor(type: RenderType): string | boolean {
  switch (type) {
    case "number":
      return "agNumberColumnFilter";
    case "date":
      return "agDateColumnFilter";
    case "boolean":
      return false; // A two-value column filters faster by clicking the header.
    case "image":
      return false; // Filtering a logo means filtering its source column.
    default:
      return "agTextColumnFilter";
  }
}

function toCssStyle(
  style: ScreenerCellStyle,
  resolve: TokenResolver,
): Record<string, string> {
  const css: Record<string, string> = {};
  if (style.fg) css["color"] = resolve(style.fg);
  if (style.bg) css["backgroundColor"] = resolve(style.bg);
  if (style.bold) css["fontWeight"] = "600";
  return css;
}

/**
 * Can this computed column actually produce values here?
 *
 * `requires[]` is the declared input list; when absent it is derived from the
 * expression, so a column authored before the field existed still gates
 * correctly. A screen that lacks an input is not an error — screens differ, and
 * a saved column simply doesn't apply to all of them.
 */
export function canCompute(
  column: ScreenerColumn,
  available: ReadonlySet<string> | ReadonlyMap<string, unknown>,
): boolean {
  if (!column.computed) return false;
  const required =
    column.computed.requires ?? [...collectFields(column.computed.expression)];
  // An empty name is an *unfilled slot*, not a missing input. Treating it as
  // missing hides the column the instant a user picks "column" in the editor and
  // before they choose which one — so the row they are building disappears and
  // cannot be finished. A screen genuinely lacking an input is a different
  // thing, and still gates.
  return required.filter((name) => name !== "").every(hasField(available));
}

/**
 * Can this image column actually render?
 *
 * An image column's content comes from its URL *template*, not from a field of
 * its own — so `field` is only an id, and what has to exist is whatever the
 * template interpolates. That is what lets a logo column sit beside the `symbol`
 * column reading the same value, instead of colliding with it on one field.
 */
export function canRenderImage(
  column: ScreenerColumn,
  available: ReadonlySet<string> | ReadonlyMap<string, unknown>,
): boolean {
  if (column.type !== "image") return false;
  return imageFields(column.imageUrl).every(hasField(available));
}

const hasField =
  (available: ReadonlySet<string> | ReadonlyMap<string, unknown>) =>
  (field: string): boolean =>
    available instanceof Set
      ? available.has(field)
      : (available as ReadonlyMap<string, unknown>).has(field);

/**
 * Is this `when` a tier-1 predicate, or a tier-2 filter node?
 *
 * Structural, because a predicate is *literally* a condition minus its `field` —
 * that is what makes the tier-2 widening a superset rather than a breaking
 * change. A node carrying `field` or `children` addresses the row; anything else
 * addresses this cell's own value.
 */
export function isRowScoped(
  when: ScreenerFormatRule["when"],
): when is ScreenerFilterNode {
  // `kind`: a ScanScript condition, which reads the row like any filter node.
  return "field" in when || "children" in when || "kind" in when;
}

/**
 * Build the `cellStyle` callback for a column's formatting.
 *
 * `cellStyle` rather than `cellClassRules` because the contract carries semantic
 * theme tokens, not class names: resolving them here keeps a theme switch a
 * re-render instead of a stylesheet the contract would have to know about.
 *
 * Three tiers, mirroring the plot palette (D7):
 *
 * 1. `when` is a predicate against **this cell's own value** — `%chg` red/green
 * 2. `when` is a filter node against **the whole row** — colour `symbol` by
 *    `changePercent`, or any AND/OR combination
 * 3. `colorBy` is an expression returning a **theme token**, which overrides the
 *    rule list entirely
 *
 * Within tiers 1–2 rules are evaluated in order and the last match wins, so a
 * later rule can refine an earlier one. Tier 3 is not a further rule: it is a
 * different way of deciding, so it short-circuits rather than merging.
 *
 * All of it runs client-side, which is *why* the AST is contract data — a theme
 * switch must restyle without a round-trip, so evaluation can never live on the
 * server (D13).
 */
function cellStyleFor(
  column: ScreenerColumn,
  resolve: TokenResolver,
): ColDef<ScreenerRow>["cellStyle"] | undefined {
  const rules = column.format;
  const colorBy = column.colorBy;
  if (!rules?.length && !colorBy) return undefined;

  return (params) => {
    if (colorBy) {
      const token = evaluate(colorBy, params.data ?? {});
      // A null token means "no opinion for this row", not an error — the usual
      // shape of an iff() whose branches only cover some cases.
      return token == null ? null : { color: resolve(String(token)) };
    }

    let css: Record<string, string> | undefined;
    for (const rule of rules ?? []) {
      const matched = isRowScoped(rule.when)
        ? evaluateFilterNode(rule.when, params.data ?? {})
        : testPredicate(rule.when, params.value);
      if (matched) {
        css = { ...(css ?? {}), ...toCssStyle(rule.style, resolve) };
      }
    }
    // AG Grid needs an explicit null to clear styling from a previous render.
    return css ?? null;
  };
}

function valueFormatterFor(
  type: RenderType,
  precision: number | undefined,
  /** Asked per render, so a column found to hold only -1/0/1 switches live. */
  whole?: () => boolean,
): ColDef<ScreenerRow>["valueFormatter"] | undefined {
  if (type === "date") {
    return ({ value }) =>
      value instanceof Date ? value.toISOString().slice(0, 10) : (value ?? "");
  }
  if (type === "number") {
    // Per *column*, never per value. Falling back to "no decimals when it is a
    // whole number" would make 7 and 7.25 render differently in one column, and
    // a column of numbers that do not line up is harder to read than one with a
    // trailing zero in it.
    return ({ value }) =>
      typeof value === "number"
        ? value.toFixed(precision ?? (whole?.() ? 0 : DEFAULT_PRECISION))
        : "";
  }
  return undefined;
}

/**
 * Whether every value a column holds is -1, 0 or 1 — an event or a flag, which
 * reads as a whole number (owner, 2026-10-06). Needs at least one value: an
 * empty column says nothing.
 */
export function isEventColumn(rows: readonly ScreenerRow[], field: string): boolean {
  let seen = false;
  for (const row of rows) {
    const v = (row as Record<string, unknown>)[field];
    if (v === null || v === undefined) continue;
    if (v !== -1 && v !== 0 && v !== 1) return false;
    seen = true;
  }
  return seen;
}

/**
 * Whole-number columns by their name: an **Age**, a **Since** or a **Count** is
 * a number of bars, and `1.00` beside it is noise (owner, 2026-10-06). Read
 * from the name because nothing in the data can say it — computed columns are
 * stored as float32, so a count and a price arrive as the same type. Still
 * per *column*, as `valueFormatterFor` requires: every cell of an Age column
 * prints without decimals. A Decimals value set on the column wins.
 */
const COUNT_FIELD = /(Age|Since|Count)$/;

export function countPrecision(field: string, meta?: ScreenerColumnMeta): number | undefined {
  return COUNT_FIELD.test(meta?.baseField || field) || COUNT_FIELD.test(field) ? 0 : undefined;
}

/** Turn one configured or discovered column into a Community `ColDef`. */
function toColDef(
  field: string,
  type: RenderType,
  column: ScreenerColumn | undefined,
  hidden: boolean,
  resolve: TokenResolver,
  meta?: ScreenerColumnMeta,
  locale?: string,
  categoryOf?: CategoryOf,
  metaOf?: (field: string) => ScreenerColumnMeta | undefined,
  wholeNumber?: (field: string) => boolean,
): ColDef<ScreenerRow> {
  // Header precedence: contract override, then the server's short label, then
  // the field name. `label` is empty until the dictionary lands.
  const header = column?.header || meta?.label || field;

  // **The period rides the band now** (docs/plans/screener-bands.md): a band's
  // key includes its period, so a weekly column never shares a band with its
  // daily twin, and the band's small-caps prefix says which. The per-column
  // chip went with it; the tooltip still spells the period out.
  const badge = meta?.timeframe ? formatTimeframe(meta.timeframe).toUpperCase() : "";
  const freshness = freshnessOf(column, meta);

  const def: ColDef<ScreenerRow> = {
    field,
    headerName: header,
    hide: hidden,
    sortable: true,
    resizable: true,
    filter: filterFor(type),
  };

  // The tooltip carries the friendly name and the period spelled out, since the
  // header itself only has room for an abbreviation and a chip.
  const tooltip = [
    meta?.description || "",
    badge ? `${badge} timeframe` : "",
    freshness === "live" ? "live while the market is" : freshness === "computed" ? "computed each minute" : "",
  ]
    .filter(Boolean)
    .join(" · ");
  if (tooltip) def.headerTooltip = tooltip;
  const mark = markClass(freshness);
  if (mark) def.headerClass = mark;
  // Read by `groupIntoBands` through the grid; AG Grid carries `context` untouched.
  def.context = { band: columnBand(column, meta, categoryOf, metaOf) };

  // A linked column wears a 🔗 after its name (`screen-links.ts`). A header
  // param rather than text in `headerName`, which `columnsFromDefs` reads back
  // into `header` — the mark would be saved as part of the name.
  if (column?.link) def.headerComponentParams = { linked: true };

  if (column?.width !== undefined) def.width = column.width;
  if (column?.pinned) def.pinned = column.pinned;
  if (type === "number") def.type = "numericColumn";

  if (type === "image") {
    const template = column?.imageUrl;
    def.cellRenderer = ImageCell;
    def.cellRendererParams = { template };
    // `field` is only an id here — the renderer reads the row through the
    // template — so bind by colId. That is what lets a logo column sit beside
    // the column it reads from without two ColDefs colliding on one field.
    delete def.field;
    def.colId = field;
    // Sorting a column of logos would sort by whatever the underlying field
    // happens to be, which is not what the header suggests.
    def.sortable = false;
    def.resizable = true;
    // Just enough for a logo at typical row heights. The image scales to the
    // cell, so widening the column grows the icon rather than padding it.
    // `initialWidth`, for the boolean column's reason below.
    if (column?.width === undefined) def.initialWidth = 32;
    def.minWidth = 24;
    // The cell IS the image — no default cell padding, or the logo shrinks to
    // whatever is left over rather than filling the space it was given.
    def.cellStyle = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
    };
  }

  // The Movers News cell reads its `*Tag`, `*At` and `*Count` siblings off the
  // row (`lib/news-cell.ts`); `field` stays, so it still sorts and filters by
  // the headline text.
  if (isNewsField(field)) {
    def.cellRenderer = NewsCell;
    if (column?.width === undefined) def.initialWidth = 320;
  }

  if (type === "boolean") {
    // A checkbox instead of the words `true` and `false` — see `BooleanCell`
    // for why, and for why a missing value renders as neither.
    def.cellRenderer = BooleanCell;
    // Centred, because the mark is a symbol rather than a value: left-aligned it
    // would sit under a header whose text starts at the same x and read as a
    // truncated word. `field` stays, unlike the image column's — this renderer
    // reads the cell it is bound to, so sorting and filtering still work.
    def.cellStyle = { display: "flex", alignItems: "center", justifyContent: "center" };
    // A checkbox needs no more room than its box plus the gutter, and a boolean
    // header is usually longer than the mark under it.
    //
    // **`initialWidth`, never `width`, for a default.** AG Grid re-applies
    // `width` every time the defs are rebuilt, and "Fit widths to data" rebuilds
    // them — so the checkbox columns snapped back to 60 over their measured ~35
    // and Fit disagreed with a fresh load (owner, 2026-10-05). `initialWidth`
    // applies when the column is created and never again.
    if (column?.width === undefined) def.initialWidth = 60;
    def.minWidth = 34;
  }

  const formatter = valueFormatterFor(
    type,
    column?.precision ?? countPrecision(field, meta),
    wholeNumber ? () => wholeNumber(field) : undefined,
  );
  if (formatter) def.valueFormatter = formatter;

  // Presentation only — the cell keeps its number, so sorting, filtering and
  // expressions all still see the real magnitude.
  //
  // `effectiveNotation` adds the K / M / B default for volume-like fields, so a
  // column nobody configured still reads `214.31M`.
  const notation = effectiveNotation(column, meta?.baseField || field);
  if (notation && notation !== "plain") {
    def.valueFormatter = (params) =>
      formatNotation(params.value, notation, column?.precision, locale) ??
      (params.value == null ? "" : String(params.value));
  }

  const cellStyle = column ? cellStyleFor(column, resolve) : undefined;
  if (cellStyle) def.cellStyle = cellStyle;

  // A computed column has no Arrow field behind it, so its value comes from the
  // expression. AG Grid sorts and filters on the getter's result, which is what
  // makes a derived column a first-class one for free.
  if (column?.computed) {
    const expression = column.computed.expression;
    // A history-needing expression was materialised server-side and arrives as a
    // real column, so it binds to the field like any other. Installing a getter
    // would evaluate it against a single row, get null, and overwrite the value
    // the server just sent — the column would go blank for the one reason it
    // exists.
    if (!needsHistory(expression)) {
      def.valueGetter = (params) => evaluate(expression, params.data ?? {});
      // `field` would otherwise shadow the getter for a name that happens to
      // collide with a real column.
      delete def.field;
      def.colId = field;
    }
  }

  return def;
}

export interface BuildColumnDefsOptions {
  /** Fields discovered in the Arrow schema — the authority on what exists. */
  fields: readonly ScreenerField[];
  /** The contract's column view. Omitted or empty shows everything. */
  columns?: readonly ScreenerColumn[];
  /** Resolves semantic color tokens; defaults to passing the token through. */
  resolveToken?: TokenResolver;
  /** Per-column metadata from qv-data, supplying labels and timeframes. */
  meta?: readonly ScreenerColumnMeta[];
  /** An indicator's catalog category, for a computed column's default band. */
  categoryOf?: CategoryOf;
  /**
   * Stored fields with no value yet that a server said are coming
   * (`source: compute`) — the Impulse columns in session, before wave 2 lands.
   * Kept as columns, drawn pending, rather than skipped as dropped.
   */
  awaited?: ReadonlySet<string>;
  /**
   * BCP 47 for the **screened market**, used only by `compact` notation.
   *
   * One per grid rather than one per row, because a screener shows one market
   * at a time (D40). An Indian screen then reads in lakh and crore instead of
   * K/M/B; omitted, everything behaves exactly as it did.
   */
  locale?: string;
  /**
   * Whether a number column holds only whole events — every value -1, 0 or 1,
   * as Bongo, JAS Timing and the DOTS flags do (owner, 2026-10-06) — so it
   * prints without decimals. A *lookup*, asked as each cell renders, so the
   * host can learn it from the data without rebuilding the column definitions.
   */
  wholeNumber?: (field: string) => boolean;
}

/**
 * Build the full `ColDef[]`: configured columns first in their configured order,
 * then every other discovered field, hidden but present so it can be toggled on.
 *
 * With no configured columns every field shows, which is the right default for a
 * screen nobody has customised yet.
 */
export function buildColumnDefs({
  fields,
  columns,
  resolveToken = passthroughResolver,
  meta = [],
  locale,
  categoryOf,
  awaited,
  wholeNumber,
}: BuildColumnDefsOptions): ColDef<ScreenerRow>[] {
  const typeByField = new Map(fields.map((f) => [f.name, f.type]));
  const metaByField = new Map(meta.map((m) => [m.field, m]));
  const metaOf = (field: string) => metaByField.get(field);
  const configured = columns ?? [];
  const hasView = configured.length > 0;

  const defs: ColDef<ScreenerRow>[] = [];
  const placed = new Set<string>();

  for (const column of configured) {
    const discovered = typeByField.get(column.field);

    if (column.computed) {
      // A computed column has no Arrow field of its own; what it needs is its
      // *inputs*. Missing one means the expression could only ever produce
      // nulls, so skip it for the same reason a dropped upstream column is
      // skipped — an empty column that can never fill is worse than absence.
      if (!canCompute(column, typeByField)) continue;
    } else if (column.type === "image") {
      // Likewise an image column: its `field` is only an id, and what must
      // exist is whatever its URL template reads.
      if (!canRenderImage(column, typeByField)) continue;
    } else if (discovered === undefined && !awaited?.has(column.field)) {
      // Configured but no longer in the result — the upstream engine dropped or
      // renamed it. Skip rather than render a permanently empty column. One a
      // server said is coming (`awaited`) is not dropped, only not here yet.
      continue;
    }
    // **One def per field, whatever the config says twice.** Nothing upstream
    // should put a field in here twice, but a config that has picked one up —
    // and one had — made AG Grid dedupe the second by inventing `field_1`,
    // which then came back through `columnsFromDefs` as a column of its own.
    // Skipping here means the grid is never asked to invent an id, so the loop
    // that created those cannot run again.
    if (placed.has(column.field)) continue;
    placed.add(column.field);
    defs.push(
      toColDef(
        column.field,
        // A computed column has no Arrow type to infer from, so it defaults to
        // number — the scalar catalog is arithmetic — and the contract's `type`
        // overrides when the expression yields text (an iff over labels, say).
        column.type ?? discovered ?? "number",
        column,
        column.hide === true,
        resolveToken,
        metaByField.get(column.field),
        locale,
        categoryOf,
        metaOf,
        wholeNumber,
      ),
    );
  }

  for (const field of fields) {
    if (placed.has(field.name)) continue;
    // Undiscovered-by-the-view fields stay available but hidden — unless there
    // is no view at all, in which case showing everything is the sane default.
    defs.push(
      toColDef(
        field.name,
        field.type,
        undefined,
        hasView,
        resolveToken,
        metaByField.get(field.name),
        locale,
        categoryOf,
        metaOf,
        wholeNumber,
      ),
    );
  }

  return defs;
}

/**
 * Read the current column view back out of the grid, contract-shaped.
 *
 * Every discovered field is emitted, including hidden ones, so ordering and
 * per-column overrides survive a column being toggled off and back on.
 */
/**
 * The id the removed per-row actions column used.
 *
 * The column itself is gone — it carried CHART and NEWS buttons that were never
 * asked for — but the guard below outlives it. A workspace saved by a client
 * that still had the column can carry `__rowActions` in `ScreenerConfig.columns`,
 * and this client has no renderer for it: dropping it on the way in is what
 * stops it rendering as an unexplained empty column pinned to the left.
 */
export const ROW_ACTIONS_FIELD = "__rowActions";

/**
 * The id of the track-list star column.
 *
 * Injected by `ScreenerGrid`, never by `buildColumnDefs` — that function is pure
 * over frame plus config, and this column needs callbacks. It carries no data,
 * is not in the frame, and is not a column the user chose, so like
 * `ROW_ACTIONS_FIELD` it must never round-trip into `ScreenerConfig.columns`:
 * a saved config naming it would come back to a client that renders it as an
 * unexplained empty column, and the *user's* column order would then contain a
 * slot nothing can fill.
 *
 * Double underscore for the same reason that one does — no server field can
 * collide with it, because normalisation produces camelCase.
 */
export const TRACK_LIST_FIELD = "__trackList";

/**
 * Where the star column goes: **after the identity block, not in front of it.**
 *
 * The logo and the ticker are one thing — the row's identity, read as a pair —
 * and an action column driven between them costs a beat of reading on every row
 * forever. So this lands after the last left-pinned column rather than at index
 * 0, which is also what keeps the star inside the pinned block and therefore on
 * screen at any horizontal scroll.
 *
 * Not a constant `2`, because a config need not carry a logo: a screen showing
 * only `symbol` would otherwise get the star in front of the ticker, which is
 * the arrangement this function exists to avoid. With nothing pinned at all it
 * falls to index 1 — after the first column, whatever that is — and to 0 when
 * there are no columns yet.
 *
 * Pure, and here rather than inside the component, because the edge cases are
 * exactly the part that cannot be seen in a browser: a grid with no logo and a
 * grid with no columns both render something plausible either way.
 */
export function trackColumnIndex(defs: readonly ColDef<ScreenerRow>[]): number {
  let lastPinned = -1;
  for (let i = 0; i < defs.length; i += 1) {
    if (defs[i]!.pinned === "left") lastPinned = i;
  }
  if (lastPinned >= 0) return lastPinned + 1;
  return Math.min(1, defs.length);
}

export function columnsFromDefs(
  defs: readonly ColDef<ScreenerRow>[],
  previous: readonly ScreenerColumn[] = [],
): ScreenerColumn[] {
  const byField = new Map(previous.map((c) => [c.field, c]));
  const out: ScreenerColumn[] = [];
  for (const def of defs) {
    // **`field` first, `colId` only as the fallback — and the order is the fix.**
    //
    // A client-computed column and an image column deliberately have no `field`
    // (their value comes from a getter or a URL template), so `colId` has to
    // answer for them, and keying on `field` alone dropped them from the config
    // the first time the grid emitted. But `colId` is not always ours: AG Grid
    // *generates* one, and when two defs collide on a field it makes the second
    // unique by appending `_1`. Reading that back as a field wrote an invented
    // name into the workspace — `name_1`, `open_1`, `volume_1` — which matches
    // no Arrow column, renders nothing, and stays there.
    //
    // Preferring the real `field` cannot mint a name: it is the one the server
    // sent. The fallback still covers the two kinds that have none.
    const field = def.field ?? def.colId;
    if (!field) continue;
    // Furniture from a column that no longer exists — see ROW_ACTIONS_FIELD.
    if (field === ROW_ACTIONS_FIELD) continue;
    // Furniture the grid injects rather than the user chose — see TRACK_LIST_FIELD.
    if (field === TRACK_LIST_FIELD) continue;

    // Carry forward everything the grid does not model. `computed`, `type`,
    // `imageUrl`, `format` and `colorBy` exist only in the contract; rebuilding
    // a column from ColDefs alone silently discarded them, so resizing a column
    // erased the expression behind it.
    const column: ScreenerColumn = { ...byField.get(field), field };

    // The grid *is* the authority on these five, so each is reasserted — set
    // when the def says so, cleared when it does not. Merging without clearing
    // would make un-pinning or un-hiding impossible.
    if (def.headerName && def.headerName !== field) column.header = def.headerName;
    else delete column.header;
    if (def.hide) column.hide = true;
    else delete column.hide;
    if (typeof def.width === "number") column.width = def.width;
    else delete column.width;
    if (def.pinned === "left" || def.pinned === "right") column.pinned = def.pinned;
    else delete column.pinned;

    out.push(column);
  }
  return out;
}
